import { useGetTrip_manifest } from 'queries/trip_manifest.query';
import type { TypedSeating } from 'queries/seating.query';
import { Box, Icon, InlineStack, Link, SkeletonBodyText, Spinner, Text, Tooltip } from '@shopify/polaris';
import { PackageIcon, PersonIcon } from '@shopify/polaris-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faCreditCard,
  faClone,
  faDongSign,
  faLock,
  faParachuteBox,
  faPen,
  faPercent,
  faRemove,
  faRightLeft,
  faScissors,
  faStar,
  faTaxi,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { useEffect, useMemo, useState } from 'react';
import { ENUM_ORDER_INVOICE_TYPE, ENUM_ORDER_PAYMENT_STATUS, ENUM_ORDER_STATUS, useGetOrders } from 'queries/orders.query';
import { ENUM_TRANSACTION_PAYMENT_METHOD } from 'queries/transactions.query';
import HoverMarquee from 'components/HoverMarquee';
import helpers from 'helpers/index';
import { useDeleteConfirm } from 'context/DeleteConfirmContext';
import { useNotification } from 'context/NotificationContext';
import EditOrderModal from 'entities/orders/edit_order.modal';
import PaymentOrderModal from 'entities/orders/payment_order.modal';
import EditOrderTaxModal from 'entities/ticket-managers/components/editOrderTax.modal';
import { callStatusCallbackForDisplay } from 'entities/ticket-managers/components/ticket_helper';
import dayjs from 'utils/dayjs';
import {
  ENUM_TICKET_TYPE,
  useMutateTicketClone,
  useMutateTicketDetach,
  useMutateTicketMarkAsPriority,
  useMutateTicketTemporaryDelete,
} from 'queries/ticket.query';
import type { TypedTripData } from 'queries/trip.query';
import AddTicketModal from 'entities/ticket-managers/add_ticket.modal';

type DispatchTripOrderListProps = {
  tripManifestId: string;
  tripData: TypedTripData;
  seating?: TypedSeating;
  movingTicketIds: string[];
  movingTickets?: boolean;
  onMoveToSeat?: (position: string) => void;
  onSelectTicketsToMove: (ticketIds: string[]) => void;
  onPickupAddressClick?: (pickupPlaceId: string) => void;
  onDropoffAddressClick?: (dropoffPlaceId: string) => void;
};

export default function DispatchTripOrderList({
  tripManifestId,
  tripData,
  seating,
  movingTicketIds,
  movingTickets = false,
  onMoveToSeat,
  onSelectTicketsToMove,
  onPickupAddressClick,
  onDropoffAddressClick,
}: DispatchTripOrderListProps) {
  const needsSeatMap = !seating?.seating_available || seating?.seating_number == null || seating?.seating_available_count == null;
  const { data: manifestDetail, isLoading: loadingSeatMap, isError: seatMapError } = useGetTrip_manifest(needsSeatMap ? tripManifestId : undefined);
  const seatLayout = needsSeatMap ? manifestDetail?.seating : seating;

  const {
    data: orderResponse,
    isLoading,
    isError,
    refetch,
  } = useGetOrders(
    {
      trip_manifest_id: tripManifestId,
      limit: 400,
      sort: 'createdAt:asc',
    },
    Boolean(tripManifestId)
  );
  const { mutateAsync: cancelTicket } = useMutateTicketTemporaryDelete();
  const { mutateAsync: cloneTicket } = useMutateTicketClone();
  const { mutateAsync: detachTicket } = useMutateTicketDetach();
  const { mutateAsync: toggleTicketPriority, isPending: togglingPriority, variables: togglingPriorityTicketId } = useMutateTicketMarkAsPriority();
  const { showDeleteConfirm } = useDeleteConfirm();
  const { addNotification } = useNotification();
  const [cancellingTicketId, setCancellingTicketId] = useState('');
  const [cloningTicketId, setCloningTicketId] = useState('');
  const [detachingTicketId, setDetachingTicketId] = useState('');
  const [editingOrderId, setEditingOrderId] = useState('');
  const [payingOrderId, setPayingOrderId] = useState('');
  const [taxOrderId, setTaxOrderId] = useState('');
  const [highlightedTicketId, setHighlightedTicketId] = useState(() => window.location.hash.replace(/^#/, '').trim());
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [selectedTicketId, setSelectedTicketId] = useState('');
  const [selectedEmptyPositions, setSelectedEmptyPositions] = useState<string[]>([]);
  const [passengerBookingPositions, setPassengerBookingPositions] = useState<string[]>([]);
  const [showPassengerModal, setShowPassengerModal] = useState(false);
  const [showShipmentModal, setShowShipmentModal] = useState(false);

  useEffect(() => {
    const updateHighlightedTicket = () => setHighlightedTicketId(window.location.hash.replace(/^#/, '').trim());
    window.addEventListener('hashchange', updateHighlightedTicket);
    return () => window.removeEventListener('hashchange', updateHighlightedTicket);
  }, []);

  const handleCancelTicket = async (ticketId: string) => {
    const confirmed = await showDeleteConfirm(
      'Bạn có chắc muốn hủy vé này?',
      'Việc hủy vé có thể được thông báo cho khách hàng qua Zalo. Tài xế cũng có thể nhận được các thông báo tương tự.'
    );
    if (!confirmed) return;
    setCancellingTicketId(ticketId);
    try {
      await cancelTicket(ticketId);
      await refetch();
      addNotification('info', 'Đã hủy vé.');
    } catch (error: any) {
      addNotification('error', error?.message || 'Không thể hủy vé.');
    } finally {
      setCancellingTicketId('');
    }
  };

  const handleCloneTicket = async (ticketId: string) => {
    setCloningTicketId(ticketId);
    try {
      const clonedTicket = await cloneTicket(ticketId);
      if (!clonedTicket.ticket_id) throw new Error('Không nhận được mã vé mới.');
      onSelectTicketsToMove([String(clonedTicket.ticket_id)]);
      addNotification('info', 'Đã nhân đôi vé. Hãy chọn ghế cho vé mới.');
    } catch (error: any) {
      addNotification('error', error?.message || 'Không thể nhân đôi vé.');
    } finally {
      setCloningTicketId('');
    }
  };

  const handleDetachTicket = async (ticketId: string) => {
    setDetachingTicketId(ticketId);
    try {
      await detachTicket(ticketId);
      setSelectedOrderId('');
      setHighlightedTicketId('');
      setSelectedTicketId(ticketId);
      await refetch();
      addNotification('info', 'Tách vé thành công!');
    } catch (error: any) {
      addNotification('error', error?.message || 'Không thể tách vé.');
    } finally {
      setDetachingTicketId('');
    }
  };

  const handleToggleTicketPriority = async (ticketId: string, isPriority: boolean) => {
    try {
      await toggleTicketPriority(ticketId);
      await refetch();
      addNotification('info', isPriority ? 'Đã bỏ đánh dấu vé ưu tiên.' : 'Đã đánh dấu vé ưu tiên.');
    } catch (error: any) {
      addNotification('error', error?.response?.data?.message || error?.message || 'Không thể cập nhật trạng thái ưu tiên.');
    }
  };

  const orders = useMemo(
    () =>
      (orderResponse?.entities ?? []).filter((order) => {
        if (order.order_status === ENUM_ORDER_STATUS.CANCELLED || (order.ticket as unknown) === 0) return false;
        if (!Array.isArray(order.ticket)) return true;
        return order.ticket.some((ticket) => Number(ticket.markAsDeleted) !== 1);
      }),
    [orderResponse?.entities]
  );
  const ticketRows = useMemo(
    () =>
      orders.flatMap((order) =>
        (Array.isArray(order.ticket) ? order.ticket : [])
          .filter((ticket) => Number(ticket.markAsDeleted) !== 1 && String(ticket.seat_position || '').trim())
          .map((ticket) => ({ order, ticket }))
      ),
    [orders]
  );
  const highlightedOrderId = useMemo(() => {
    if (!highlightedTicketId) return '';
    const highlightedRow = ticketRows.find(({ ticket }) => String(ticket.ticket_id) === highlightedTicketId);
    return highlightedRow ? String(highlightedRow.order.id || highlightedRow.ticket.order_id || '') : '';
  }, [highlightedTicketId, ticketRows]);

  if (!tripManifestId) {
    return (
      <Text as="p" tone="subdued">
        Chuyến chưa có lệnh điều xe nên chưa có danh sách đơn hàng.
      </Text>
    );
  }

  if (isLoading) {
    return (
      <Box padding="300" background="bg-surface" borderRadius="200">
        <SkeletonBodyText lines={1} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Text as="p" tone="critical">
        Không thể tải danh sách đơn hàng.
      </Text>
    );
  }

  const passengerTickets = ticketRows
    .map(({ ticket }) => ticket)
    .filter((ticket) => ticket.seat_position !== 'trunk' && ticket.ticket_type !== 'SHIPMENT');
  const occupiedPositions = new Set(passengerTickets.map((ticket) => ticket.seat_position));
  const seatCount = Number(seatLayout?.seating_number || 0);
  const availableCount = Number(seatLayout?.seating_available_count || 0);
  // Keep the original indexes: removing empty entries would shift the seat codes.
  const positions = (seatLayout?.seating_available || '').split('|');
  const emptySeats = Array.from({ length: seatCount }, (_, index) => ({
    position: positions[index] || undefined,
    number: index + 1,
    disabled: index >= availableCount || !positions[index],
  })).filter(({ position, disabled }) => disabled || !occupiedPositions.has(position));

  return (
    <div className="dispatch-trip-orders">
      <Text as="h3" variant="headingSm">
        Danh sách vé ({ticketRows.length})
      </Text>
      <div className="dispatch-trip-orders__list">
        {ticketRows.map(({ order, ticket }, index) => {
          const ticketId = String(ticket.ticket_id || '');
          const orderId = String(order.id || ticket.order_id || '');
          const isHighlighted = selectedTicketId === ticketId || Boolean(orderId && (selectedOrderId === orderId || highlightedOrderId === orderId));
          const isShipment = ticket.seat_position === 'trunk' || ticket.ticket_type === 'SHIPMENT';
          const customerName = order.customer_data?.display_name || order.customer_name || order.pickup_contact_name || 'Khách chưa có tên';
          const customerPhone = order.customer_data?.user_phonenumber || order.customer_phone || order.pickup_contact_phonenumber || '';
          const customerId = order.customer_data?.user_id || order.customer_id;
          const hasBankTransaction = !!order.transactions?.some((transaction) => transaction.payment_method === ENUM_TRANSACTION_PAYMENT_METHOD.BANK);
          const hasDebitTransaction = !!order.transactions?.some((transaction) =>
            [
              ENUM_TRANSACTION_PAYMENT_METHOD.DEBT_AGENT,
              ENUM_TRANSACTION_PAYMENT_METHOD.DEBT_CUSTOMER,
              ENUM_TRANSACTION_PAYMENT_METHOD.THIRD_PARTY,
            ].includes(transaction.payment_method)
          );
          const paidFull = Number(order.total_paid) >= Number(order.total_must_pay);
          const hasPayment = order.payment_status !== ENUM_ORDER_PAYMENT_STATUS.UNPAID;
          const isLocked = Number(order.is_locked) === 1;
          const isPriority = Number(ticket.mark_as_priority) === 1;
          const isMoving = Boolean(ticketId && movingTicketIds.includes(ticketId));
          const taxColor = !order.order_invoice_info
            ? 'var(--p-color-icon-secondary)'
            : order.order_invoice_info.tax_type === ENUM_ORDER_INVOICE_TYPE.COMPANY
              ? 'var(--p-color-icon-critical)'
              : 'var(--p-color-icon-emphasis)';

          return (
            <div
              className={`dispatch-trip-order ${isHighlighted ? 'dispatch-trip-order--highlighted' : ''} ${isMoving ? 'dispatch-trip-order--moving' : ''}`}
              key={ticketId || `${order.id}-${index}`}
              onClick={(event) => {
                if ((event.target as HTMLElement).closest('button, a')) return;
                setSelectedTicketId('');
                setSelectedOrderId(orderId);
                setSelectedEmptyPositions([]);
              }}
            >
              <div className={`dispatch-trip-order__position ${isShipment ? 'dispatch-trip-order__position--shipment' : ''}`}>
                {isShipment ? (
                  <Tooltip content={`Hàng hóa · Vị trí ${ticket.seat_position}`}>
                    <Icon source={PackageIcon} />
                  </Tooltip>
                ) : (
                  <Tooltip content={`Hành khách · Ghế ${ticket.seat_position}`}>
                    <Icon source={PersonIcon} />
                  </Tooltip>
                )}
              </div>
              <div className="dispatch-trip-order__content">
                <div className="dispatch-trip-order__customer-row">
                  <HoverMarquee className="dispatch-trip-order__customer-marquee" speed={60}>
                    <Text as="span" fontWeight="bold">
                      {customerId ? (
                        <Link removeUnderline url={`/customer/view/${customerId}`}>
                          {customerName}
                        </Link>
                      ) : (
                        customerName
                      )}
                      {customerPhone ? ` · ${customerPhone}` : ''}
                    </Text>
                  </HoverMarquee>
                  <div className="dispatch-trip-order__code">
                    <InlineStack gap="100" blockAlign="center" wrap={false}>
                      <Text as="span" tone="subdued" fontWeight="semibold">
                        <Link removeUnderline url={`/orders/view/${order?.id}`}>
                          {order?.order_code}
                        </Link>
                      </Text>
                    </InlineStack>
                  </div>
                </div>
                <div className="dispatch-trip-order__route">
                  <div className="dispatch-trip-order__route-point dispatch-trip-order__route-point--pickup">
                    <span className="dispatch-trip-order__route-dot" aria-hidden="true" />
                    {order.pickup_place_id && onPickupAddressClick ? (
                      <button
                        type="button"
                        className="dispatch-trip-order__address-button"
                        onClick={() => onPickupAddressClick(order.pickup_place_id as string)}
                        title="Đi tới điểm đón này trên bản đồ"
                      >
                        {order.pickup_address || 'Chưa có điểm đón'}
                      </button>
                    ) : (
                      <Text as="span" variant="bodySm" tone="subdued">
                        {order.pickup_address || 'Chưa có điểm đón'}
                      </Text>
                    )}
                  </div>
                  <div className="dispatch-trip-order__route-point dispatch-trip-order__route-point--dropoff">
                    <span className="dispatch-trip-order__route-dot" aria-hidden="true" />
                    {order.dropoff_place_id && onDropoffAddressClick ? (
                      <button
                        type="button"
                        className="dispatch-trip-order__address-button"
                        onClick={() => onDropoffAddressClick(order.dropoff_place_id as string)}
                        title="Đi tới điểm trả này trên bản đồ"
                      >
                        {order.dropoff_address || 'Chưa có điểm trả'}
                      </button>
                    ) : (
                      <Text as="span" variant="bodySm" tone="subdued">
                        {order.dropoff_address || 'Chưa có điểm trả'}
                      </Text>
                    )}
                  </div>
                </div>
              </div>
              <div className="dispatch-trip-order__actions">
                <div className="dispatch-trip-order__status-icons">
                  <Tooltip content={isPriority ? 'Ghế ưu tiên' : 'Ghế thường'}>
                    <span role="img" aria-label={isPriority ? 'Ghế ưu tiên' : 'Ghế thường'}>
                      <FontAwesomeIcon icon={faStar} color={isPriority ? '#f59e0b' : 'var(--p-color-icon-disabled)'} />
                    </span>
                  </Tooltip>
                  <Tooltip content={`Hóa đơn: ${order.invoices ? `Đã tạo, số ${order.invoices.shDon}` : 'Chưa xuất'}`}>
                    <FontAwesomeIcon
                      icon={faArrowUpRightFromSquare}
                      color={
                        order.invoices
                          ? order.invoices.is_signed
                            ? 'var(--p-color-icon-emphasis)'
                            : 'var(--p-color-icon-critical)'
                          : 'var(--p-color-icon-secondary)'
                      }
                    />
                  </Tooltip>
                  {callStatusCallbackForDisplay(
                    order.order_meta?.call_status,
                    order.order_meta?.call_last_updated_author_data?.display_name,
                    order.order_meta?.call_last_updated
                  )}
                  {order.order_meta?.pickup_by_data ? (
                    <Tooltip
                      content={`Đón bởi ${order.order_meta.pickup_by_data.display_name} lúc ${dayjs(Number(order.order_meta.pickup_at)).tz('Asia/Ho_Chi_Minh').format('HH:mm DD-MM-YYYY')}`}
                    >
                      <FontAwesomeIcon icon={faTaxi} color="var(--p-color-icon-emphasis)" />
                    </Tooltip>
                  ) : (
                    <Tooltip content="Đang chờ đón">
                      <FontAwesomeIcon icon={faTaxi} color="var(--p-color-icon-secondary)" />
                    </Tooltip>
                  )}
                  {order.order_meta?.dropoff_by_data ? (
                    <Tooltip
                      content={`Trả bởi ${order.order_meta.dropoff_by_data.display_name} lúc ${dayjs(Number(order.order_meta.dropoff_at)).tz('Asia/Ho_Chi_Minh').format('HH:mm DD-MM-YYYY')}`}
                    >
                      <FontAwesomeIcon icon={faParachuteBox} color="var(--p-color-icon-emphasis)" />
                    </Tooltip>
                  ) : (
                    <Tooltip content="Chưa xuống xe">
                      <FontAwesomeIcon icon={faParachuteBox} color="var(--p-color-icon-secondary)" />
                    </Tooltip>
                  )}
                </div>
                <div className="dispatch-trip-order__action-icons">
                  {isLocked ? (
                    <Tooltip content="Đơn hàng đã khóa">
                      <span aria-label="Đơn hàng đã khóa">
                        <FontAwesomeIcon icon={faLock} color="var(--p-color-icon-emphasis)" />
                      </span>
                    </Tooltip>
                  ) : (
                    <>
                      <Tooltip content="Cập nhật đơn">
                        <button
                          className="dispatch-trip-order__action-button"
                          type="button"
                          aria-label="Cập nhật đơn"
                          onClick={() => setEditingOrderId(String(order.id))}
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </button>
                      </Tooltip>
                      <Tooltip content={isMoving ? 'Bỏ chọn vé đang di chuyển' : 'Di chuyển vé'}>
                        <button
                          className={`dispatch-trip-order__action-button ${isMoving ? 'dispatch-trip-order__action-button--moving' : ''}`}
                          type="button"
                          aria-label={isMoving ? 'Bỏ chọn vé đang di chuyển' : 'Di chuyển vé'}
                          aria-pressed={isMoving}
                          disabled={!ticketId}
                          onClick={() => onSelectTicketsToMove(isMoving ? [] : [ticketId])}
                        >
                          <FontAwesomeIcon icon={faRightLeft} />
                        </button>
                      </Tooltip>
                      <Tooltip content="Hủy vé">
                        <button
                          className="dispatch-trip-order__action-button dispatch-trip-order__action-button--critical"
                          type="button"
                          aria-label="Hủy vé"
                          disabled={!ticketId || cancellingTicketId === ticketId}
                          onClick={() => handleCancelTicket(ticketId)}
                        >
                          {cancellingTicketId === ticketId ? (
                            <Spinner accessibilityLabel="Đang hủy vé" size="small" />
                          ) : (
                            <FontAwesomeIcon icon={faRemove} />
                          )}
                        </button>
                      </Tooltip>
                      <Tooltip content="Nhân đôi vé">
                        <button
                          className="dispatch-trip-order__action-button"
                          type="button"
                          aria-label="Nhân đôi vé"
                          disabled={!ticketId || cloningTicketId === ticketId}
                          onClick={() => handleCloneTicket(ticketId)}
                        >
                          {cloningTicketId === ticketId ? (
                            <Spinner accessibilityLabel="Đang nhân đôi vé" size="small" />
                          ) : (
                            <FontAwesomeIcon icon={faClone} />
                          )}
                        </button>
                      </Tooltip>
                      <Tooltip content={isPriority ? 'Bỏ đánh dấu ưu tiên' : 'Đánh dấu ưu tiên'}>
                        <button
                          className="dispatch-trip-order__action-button"
                          type="button"
                          aria-label={isPriority ? 'Bỏ đánh dấu ưu tiên' : 'Đánh dấu ưu tiên'}
                          aria-pressed={isPriority}
                          disabled={!ticketId || (togglingPriority && String(togglingPriorityTicketId) === ticketId)}
                          onClick={() => handleToggleTicketPriority(ticketId, isPriority)}
                        >
                          {togglingPriority && String(togglingPriorityTicketId) === ticketId ? (
                            <FontAwesomeIcon icon={faStar} color={'#f59e0b'} />
                          ) : (
                            <FontAwesomeIcon icon={faStar} />
                          )}
                        </button>
                      </Tooltip>
                      {Number(order.total_seat) > 1 && (
                        <Tooltip content="Tách vé">
                          <button
                            className="dispatch-trip-order__action-button"
                            type="button"
                            aria-label="Tách vé"
                            disabled={!ticketId || detachingTicketId === ticketId}
                            onClick={() => handleDetachTicket(ticketId)}
                          >
                            {detachingTicketId === ticketId ? (
                              <Spinner accessibilityLabel="Đang tách vé" size="small" />
                            ) : (
                              <FontAwesomeIcon icon={faScissors} />
                            )}
                          </button>
                        </Tooltip>
                      )}
                      <Tooltip
                        content={
                          hasPayment
                            ? `Đã thanh toán ${helpers.formatNumberWithCommas(order.total_paid)} / ${helpers.formatNumberWithCommas(order.total_must_pay)}`
                            : 'Chưa thanh toán'
                        }
                      >
                        <button
                          className="dispatch-trip-order__action-button"
                          type="button"
                          aria-label="Thanh toán"
                          onClick={() => setPayingOrderId(String(order.id))}
                        >
                          {!hasPayment ? (
                            <FontAwesomeIcon icon={faDongSign} color="var(--p-color-icon-secondary)" />
                          ) : hasDebitTransaction ? (
                            <FontAwesomeIcon icon={faTriangleExclamation} color="var(--p-color-icon-critical)" />
                          ) : hasBankTransaction ? (
                            <FontAwesomeIcon icon={faCreditCard} color={paidFull ? 'var(--p-color-icon-emphasis)' : 'var(--p-color-icon-critical)'} />
                          ) : (
                            <FontAwesomeIcon icon={faDongSign} color={paidFull ? 'var(--p-color-icon-emphasis)' : 'var(--p-color-icon-critical)'} />
                          )}
                        </button>
                      </Tooltip>
                      <Tooltip content={order.order_invoice_info?.tax_type || 'Thuế mặc định'}>
                        <button
                          className="dispatch-trip-order__action-button"
                          type="button"
                          aria-label="Cập nhật thông tin thuế"
                          onClick={() => setTaxOrderId(String(order.id))}
                        >
                          <FontAwesomeIcon icon={faPercent} color={taxColor} />
                        </button>
                      </Tooltip>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {emptySeats.map(({ position, number, disabled }) => (
          <div
            className={`dispatch-trip-order dispatch-trip-order--empty${
              selectedEmptyPositions.includes(position || '') ? ' dispatch-trip-order--highlighted' : ''
            }${disabled ? ' dispatch-trip-order--disabled' : movingTicketIds.length && onMoveToSeat ? ' dispatch-trip-order--move-target' : ''}`}
            key={`${number}-${position || 'unmapped'}`}
            role={movingTicketIds.length && onMoveToSeat && !disabled ? 'button' : undefined}
            tabIndex={movingTicketIds.length && onMoveToSeat && !disabled && !movingTickets ? 0 : undefined}
            onClick={() => {
              if (disabled || !position || movingTickets) return;
              if (movingTicketIds.length && onMoveToSeat) {
                onMoveToSeat(position);
                return;
              }
              setSelectedEmptyPositions((current) =>
                current.includes(position) ? current.filter((selectedPosition) => selectedPosition !== position) : [...current, position]
              );
              setSelectedOrderId('');
              setSelectedTicketId('');
              setHighlightedTicketId('');
            }}
            onKeyDown={(event) => {
              if ((event.key === 'Enter' || event.key === ' ') && !disabled && position && movingTicketIds.length && !movingTickets && onMoveToSeat) {
                event.preventDefault();
                onMoveToSeat(position);
              }
            }}
            data-seat-position={position}
            data-trip-manifest-id={tripManifestId}
            aria-disabled={disabled || movingTickets}
            aria-label={`Ghế ${number}${position ? ` (${position})` : ''}: ${disabled ? 'Không được phép đặt' : 'Trống'}`}
          >
            <div className="dispatch-trip-order__position">{position || number}</div>
            <div>
              <Text as="p" tone="subdued">
                {disabled
                  ? 'Ghế không được phép đặt'
                  : movingTicketIds.length && onMoveToSeat
                    ? `Chuyển ${movingTicketIds.length} vé vào ghế này`
                    : 'Ghế trống'}
              </Text>
            </div>
            <button
              type="button"
              className="dispatch-trip-order__book-button"
              aria-label={`Đặt vé ghế ${number}`}
              title={`Đặt vé ghế ${number}`}
              data-seat-position={position}
              data-trip-manifest-id={tripManifestId}
              disabled={disabled || movingTickets || movingTicketIds.length > 0}
              onClick={(event) => {
                event.stopPropagation();
                if (!position) return;
                const positionsToBook = selectedEmptyPositions.includes(position) ? selectedEmptyPositions : [...selectedEmptyPositions, position];
                setSelectedEmptyPositions(positionsToBook);
                setPassengerBookingPositions(positionsToBook);
                setShowPassengerModal(true);
              }}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <span aria-hidden="true">+</span>
            </button>
          </div>
        ))}
        <div
          className="dispatch-trip-order dispatch-trip-order--empty dispatch-trip-order--clickable"
          data-seat-position="trunk"
          data-trip-manifest-id={tripManifestId}
          role="button"
          tabIndex={0}
          aria-label="Cốp xe"
          onClick={() => setShowShipmentModal(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setShowShipmentModal(true);
            }
          }}
        >
          <div className="dispatch-trip-order__position dispatch-trip-order__position--shipment">
            <Icon source={PackageIcon} />
          </div>
          <Text as="p" tone="subdued">
            Cốp xe (Click để thêm hàng)
          </Text>
        </div>
        {loadingSeatMap && <SkeletonBodyText lines={2} />}
        {seatMapError && (
          <Text as="p" tone="critical">
            Không thể tải sơ đồ ghế.
          </Text>
        )}
        {!loadingSeatMap && !seatMapError && !seatCount && (
          <Text as="p" tone="subdued">
            Chưa có cấu hình ghế cho chuyến này.
          </Text>
        )}
      </div>
      {editingOrderId && (
        <EditOrderModal
          show
          order_id={editingOrderId}
          onClose={(reload) => {
            setEditingOrderId('');
            if (reload) refetch();
          }}
        />
      )}
      {payingOrderId && (
        <PaymentOrderModal
          show
          order_id={payingOrderId}
          onClose={(reload) => {
            setPayingOrderId('');
            if (reload) refetch();
          }}
        />
      )}
      {taxOrderId && (
        <EditOrderTaxModal
          open
          orderId={taxOrderId}
          onClose={(reload) => {
            setTaxOrderId('');
            if (reload) refetch();
          }}
        />
      )}
      {showPassengerModal && (
        <AddTicketModal
          show
          seat_position={passengerBookingPositions}
          trip_manifest_id={tripManifestId}
          tripData={tripData}
          currentTicketType={ENUM_TICKET_TYPE.PASSENGER}
          onClose={() => {
            setShowPassengerModal(false);
            setPassengerBookingPositions([]);
            setSelectedEmptyPositions([]);
            refetch();
          }}
        />
      )}
      {showShipmentModal && (
        <AddTicketModal
          show
          seat_position={['trunk']}
          trip_manifest_id={tripManifestId}
          tripData={tripData}
          currentTicketType={ENUM_TICKET_TYPE.SHIPMENT}
          onClose={() => {
            setShowShipmentModal(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}

